import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Toaster } from "sonner";
import { COMPANY, IMAGES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { trackCallConversion } from "@/lib/analytics";
import { useSeo } from "@/hooks/useSeo";

export default function Contact() {
  useSeo("Contact | StoneCrest Exterior Specialists Preston", "Contact StoneCrest Exterior Specialists in Preston, Lancashire, for residential and commercial exterior cleaning. Call +44 7307352758, email us, or request a free quote. Opening hours and location.", "https://stonecrestexteriors.co.uk/contact");
  return (
    <div className="-mt-20">
      <Toaster position="top-center" />

      {/* Dark hero band */}
      <section className="relative min-h-[46vh] flex items-end overflow-hidden" data-testid="contact-hero">
        <img src={IMAGES.contact} alt="Premium property exterior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220]/95 via-[#0b1220]/70 to-[#0b1220]/70" />
        <div className="relative px-6 md:px-12 max-w-7xl mx-auto w-full pb-14 pt-28">
          <p className="text-[11px] uppercase tracking-[0.35em] text-[var(--oxblood)] mb-4">Contact</p>
          <h1 className="font-serif font-light text-white text-5xl md:text-7xl leading-[0.98] tracking-tight max-w-2xl">Let's talk about your property.</h1>
          <p className="mt-5 text-white/75 max-w-xl">Get in touch for a free, no-obligation quote for your home or commercial premises. We aim to respond to every enquiry the same day.</p>
        </div>
      </section>

      {/* Two column */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto py-16 md:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[var(--oxblood)] mb-3">Get In Touch</p>
          <h2 className="font-serif text-3xl mb-8">Contact details</h2>
          <div className="space-y-6">
            <a href={COMPANY.phoneHref} onClick={trackCallConversion} data-testid="contact-phone" className="flex items-start gap-4 group">
              <Phone className="w-5 h-5 text-[var(--oxblood)] mt-1" strokeWidth={1.5} />
              <div><p className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">Phone</p><p className="text-lg group-hover:text-[var(--oxblood)] transition-colors">{COMPANY.phone}</p></div>
            </a>
            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-start gap-4 group">
              <Mail className="w-5 h-5 text-[var(--oxblood)] mt-1" strokeWidth={1.5} />
              <div><p className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">Email</p><p className="text-lg break-all group-hover:text-[var(--oxblood)] transition-colors">{COMPANY.email}</p></div>
            </a>
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-[var(--oxblood)] mt-1" strokeWidth={1.5} />
              <div><p className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">Location</p><p className="text-lg">{COMPANY.address}</p></div>
            </div>
            <div className="flex items-start gap-4">
              <Clock className="w-5 h-5 text-[var(--oxblood)] mt-1" strokeWidth={1.5} />
              <div className="w-full">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] mb-2">Opening Hours</p>
                {COMPANY.hours.map((h) => (
                  <div key={h.day} className="flex justify-between py-1 text-sm border-b border-[var(--border)]/60 last:border-0 max-w-xs"><span>{h.day}</span><span className="text-[var(--muted)]">{h.time}</span></div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-8 border border-[var(--border)] overflow-hidden aspect-[4/3]">
            <iframe title="StoneCrest location" src="https://www.google.com/maps?q=Percy+Street+PR1+1AW+Preston&output=embed" className="w-full h-full" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>

        <div className="lg:col-span-8">
          <Reveal>
            <div className="border border-[var(--border)] p-6 md:p-10 bg-white shadow-sm">
              <LeadForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
